import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import certificatesService from '../../services/certificatesService';
import DialogModal from '../../components/DialogModal';
import CertificateContentEditor from '../../components/certificates/CertificateContentEditor';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';

const CERT_TYPES = [
  { value: 'leaving', label: 'Leaving Certificate' },
  { value: 'transfer', label: 'Transfer Certificate' },
  { value: 'achievement', label: 'Certificate of Achievement' },
];

const PLACEHOLDERS = [
  '{{ school_name }}', '{{ student_name }}', '{{ student_id_code }}', '{{ date_of_birth }}',
  '{{ last_class_completed }}', '{{ certificate_number }}', '{{ issue_date }}',
  '{{ remarks }}', '{{ generated_date }}',
];

const emptyForm = { certificate_type: 'leaving', name: '', html_content: '', is_default: true };

function CertificateTemplatesPage() {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await certificatesService.listTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load certificate templates');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (template) => {
    setEditingId(template.id);
    setFormData({
      certificate_type: template.certificate_type,
      name: template.name,
      html_content: template.html_content,
      is_default: template.is_default,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.html_content) {
      toast.error('Name and HTML content are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        const { certificate_type, ...rest } = formData;
        await certificatesService.updateTemplate(editingId, rest);
        toast.success('Template updated');
      } else {
        await certificatesService.createTemplate(formData);
        toast.success('Template created');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await certificatesService.deleteTemplate(deleteTarget);
      toast.success('Template deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete template');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading certificate templates...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Certificate Templates</h1>
            <p>Customize the leaving, transfer, and achievement certificate layouts for your school</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Add Template
          </button>
        </div>

        <div className="table-container">
          {templates.length === 0 ? (
            <div className="empty-state">
              <p>No custom templates yet — certificates use the default built-in layout for each type.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td>{template.name}</td>
                    <td>{CERT_TYPES.find((t) => t.value === template.certificate_type)?.label}</td>
                    <td>{template.is_default ? 'Yes' : 'No'}</td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="Edit" onClick={() => openEditModal(template)}>
                        <PencilSimple size={18} />
                      </button>
                      <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteTarget(template.id)}>
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 920 }}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Template' : 'Add Template'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Certificate Type *</label>
                  <select
                    value={formData.certificate_type}
                    onChange={(e) => setFormData({ ...formData, certificate_type: e.target.value })}
                    disabled={!!editingId}
                    required
                  >
                    {CERT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    style={{ width: 'auto', marginRight: 8 }}
                  />
                  Active (used when generating this certificate type)
                </label>
              </div>

              <div className="form-group">
                <label>Template Content *</label>
                <CertificateContentEditor
                  value={formData.html_content}
                  onChange={(html) => setFormData({ ...formData, html_content: html })}
                  placeholders={PLACEHOLDERS}
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Template"
        message="This will permanently delete this certificate template. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default CertificateTemplatesPage;
